import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, map, of, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { MetaPixelService } from '../analytics/meta-pixel.service';
import { GtagService } from '../analytics/gtag.service';
import { getResponseData, getResponseMessage } from '../models/api-response.model';
import { Cart, CartItem } from '../models/cart.model';
import { Product } from '../models/product.model';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { I18nService } from './i18n.service';

/** Centralized cart API endpoints (single source of truth). */
const CART_API = {
  getMyCart: (branchId: number) => `Cart/me/${branchId}`,
  getCart: (userId: string, branchId: number) => `Cart/GetCartByUserId/${userId}/${branchId}`,
  addItem: 'CartItems/AddCartItem',
  updateItem: (id: number) => `CartItems/UpdateCartItem/${id}`,
  deleteItem: (id: number) => `CartItems/DeleteCartItem/${id}`,
} as const;

@Injectable({
  providedIn: 'root',
})
export class CartService {
  private readonly cartSubject = new BehaviorSubject<Cart | null>(null);
  readonly cart$ = this.cartSubject.asObservable();
  readonly cartCount$ = this.cart$.pipe(
    map((cart) => cart?.cartItems?.reduce((total, item) => total + item.quantity, 0) ?? 0)
  );

  /** Synchronous snapshot for in-cart checks (e.g. PDP CTA state). */
  get cartSubjectValue(): Cart | null {
    return this.cartSubject.getValue();
  }

  isProductInCart(productId: number): boolean {
    const pid = Number(productId);
    if (!pid) {
      return false;
    }
    return !!this.cartSubject
      .getValue()
      ?.cartItems?.some((item) => item.productId === pid || item.product?.id === pid);
  }

  constructor(
    private api: ApiService,
    private authService: AuthService,
    private i18n: I18nService,
    private metaPixel: MetaPixelService,
    private gtagService: GtagService
  ) {}

  /**
   * Fetches the current user's cart. Treats 404 or any error as "no cart" (empty state) so the UI never breaks.
   */
  refreshCart(): Observable<Cart | null> {
    const userId = this.authService.getUserId();
    const branchId = Number(environment.branchId) || 1;
    const endpoint = userId
      ? CART_API.getCart(userId, branchId)
      : CART_API.getMyCart(branchId);

    return this.api.get<Cart>(endpoint).pipe(
      map((response) => getResponseData(response, null as unknown as Cart)),
      map((cart) => {
        const ownerId = userId ?? cart?.userId ?? '';
        return cart && (cart.id != null && cart.id !== 0) ? cart : this.emptyCart(ownerId, branchId);
      }),
      tap((cart) => this.cartSubject.next(cart)),
      catchError(() => {
        this.cartSubject.next(null);
        return of(null);
      })
    );
  }

  private emptyCart(userId: string, branchId: number): Cart {
    return { id: 0, userId, branchId, totalAmount: 0, cartItems: [] };
  }

  /** Merges a single hydrated cart item into the in-memory cart without a round-trip GET. */
  private patchCartWithItem(item: CartItem, branchId: number): Cart {
    const current = this.cartSubject.getValue() ?? this.emptyCart(this.authService.getUserId() ?? '', branchId);
    const existingIndex = current.cartItems.findIndex((ci) => ci.id === item.id || ci.productId === item.productId);
    const cartItems =
      existingIndex >= 0
        ? current.cartItems.map((ci, idx) => (idx === existingIndex ? item : ci))
        : [...current.cartItems, item];
    const totalAmount = cartItems.reduce((sum, ci) => sum + (ci.totalPrice ?? ci.unitPrice * ci.quantity), 0);
    const next: Cart = { ...current, cartItems, totalAmount };
    this.cartSubject.next(next);
    return next;
  }

  /** Removes a cart item from the in-memory cart without a round-trip GET. */
  private patchCartRemoveItem(cartItemId: number): Cart {
    const current = this.cartSubject.getValue();
    if (!current) {
      return this.emptyCart(this.authService.getUserId() ?? '', Number(environment.branchId) || 1);
    }
    const cartItems = current.cartItems.filter((ci) => ci.id !== cartItemId);
    const totalAmount = cartItems.reduce((sum, ci) => sum + (ci.totalPrice ?? ci.unitPrice * ci.quantity), 0);
    const next: Cart = { ...current, cartItems, totalAmount };
    this.cartSubject.next(next);
    return next;
  }

  /**
   * Optimistically updates the local cart immediately, then reconciles with the POST response.
   * Callers can navigate / show success without waiting for the network round-trip.
   */
  addItem(productId: number, quantity = 1, productHint?: Product | null): Observable<void> {
    const pid = Number(productId);
    const qty = Number(quantity) || 1;
    const branchId = Number(environment.branchId) || 1;
    if (!pid || pid <= 0) {
      return throwError(() => new Error(this.i18n.t('product.invalid_product')));
    }

    const previous = this.cartSubject.getValue();
    const optimisticId = this.applyOptimisticAdd(pid, qty, branchId, productHint);

    return this.api.post<CartItem>(CART_API.addItem, { productId: pid, quantity: qty, branchId }).pipe(
      map((response) => getResponseData(response, null as unknown as CartItem)),
      tap((addedItem) => {
        if (!addedItem) {
          return;
        }
        // Drop the temp optimistic row if the server returned a different id.
        if (optimisticId < 0) {
          const current = this.cartSubject.getValue();
          if (current?.cartItems.some((ci) => ci.id === optimisticId)) {
            this.patchCartRemoveItem(optimisticId);
          }
        }
        this.patchCartWithItem(addedItem, branchId);
        if (addedItem.product) {
          this.metaPixel.trackAddToCart(addedItem.product, qty);
          this.gtagService.trackAddToCart(addedItem.product, qty);
        }
      }),
      map(() => void 0),
      catchError((err: unknown) => {
        this.cartSubject.next(previous);
        const message = this.getApiErrorMessage(err);
        return throwError(() => new Error(message));
      })
    );
  }

  /** Sync optimistic cart bump — returns temporary item id (negative) when a new row was inserted. */
  private applyOptimisticAdd(
    productId: number,
    quantity: number,
    branchId: number,
    productHint?: Product | null
  ): number {
    const current = this.cartSubject.getValue() ?? this.emptyCart(this.authService.getUserId() ?? '', branchId);
    const existingIndex = current.cartItems.findIndex(
      (ci) => ci.productId === productId || ci.product?.id === productId
    );

    if (existingIndex >= 0) {
      const existing = current.cartItems[existingIndex];
      const nextQty = existing.quantity + quantity;
      const updated: CartItem = {
        ...existing,
        quantity: nextQty,
        totalPrice: (existing.unitPrice || 0) * nextQty,
      };
      this.patchCartWithItem(updated, branchId);
      return existing.id;
    }

    const unitPrice = Number(productHint?.discountPrice ?? productHint?.price ?? 0) || 0;
    const optimisticId = -Date.now();
    const optimisticProduct: Product = {
      ...(productHint ?? ({} as Product)),
      id: productId,
      name: productHint?.name ?? productHint?.nameAr ?? '',
      price: productHint?.price ?? unitPrice,
      discountPrice: productHint?.discountPrice,
      imageUrl: productHint?.imageUrl,
    };
    const optimisticItem: CartItem = {
      id: optimisticId,
      productId,
      product: optimisticProduct,
      quantity,
      unitPrice,
      totalPrice: unitPrice * quantity,
    };
    this.patchCartWithItem(optimisticItem, branchId);
    return optimisticId;
  }

  /**
   * Optimistically updates quantity locally, then reconciles with the POST response.
   * Temp optimistic rows (id < 0) stay local until the add completes — no network call.
   */
  updateItemQuantity(cartItemId: number, quantity: number): Observable<void> {
    const branchId = Number(environment.branchId) || 1;
    const qty = Math.max(1, Number(quantity) || 1);
    const previous = this.cartSubject.getValue();
    const currentItem = previous?.cartItems?.find((ci) => ci.id === cartItemId);
    if (!currentItem) {
      return throwError(() => new Error(this.i18n.t('common.request_failed')));
    }

    const optimistic: CartItem = {
      ...currentItem,
      quantity: qty,
      totalPrice: (currentItem.unitPrice || 0) * qty,
    };
    this.patchCartWithItem(optimistic, branchId);

    // Pending optimistic add rows are not yet on the server.
    if (cartItemId < 0) {
      return of(void 0);
    }

    return this.api.post<CartItem>(CART_API.updateItem(cartItemId), {}, { quantity: qty }).pipe(
      map((response) => getResponseData(response, null as unknown as CartItem)),
      tap((updatedItem) => {
        if (updatedItem) {
          this.patchCartWithItem(updatedItem, branchId);
        }
      }),
      map(() => void 0),
      catchError((err: unknown) => {
        this.cartSubject.next(previous);
        return throwError(() => new Error(this.getApiErrorMessage(err)));
      })
    );
  }

  /**
   * Optimistically removes a line from the local cart, then DELETEs on the server.
   * Callers can update UI / toast immediately; errors restore the snapshot.
   */
  removeItem(cartItemId: number): Observable<void> {
    const previous = this.cartSubject.getValue();
    const currentItem = previous?.cartItems?.find((ci) => ci.id === cartItemId);
    this.patchCartRemoveItem(cartItemId);
    if (currentItem?.product) {
      this.gtagService.trackRemoveFromCart(currentItem.product, currentItem.quantity);
    }

    if (cartItemId < 0) {
      return of(void 0);
    }

    return this.api.delete(CART_API.deleteItem(cartItemId)).pipe(
      map(() => void 0),
      catchError((err: unknown) => {
        this.cartSubject.next(previous);
        return throwError(() => new Error(this.getApiErrorMessage(err)));
      })
    );
  }

  /** Parses API error body (camelCase/PascalCase) or falls back to status/statusText. */
  private getApiErrorMessage(err: unknown): string {
    if (err instanceof HttpErrorResponse && err.error && typeof err.error === 'object') {
      const msg = getResponseMessage(err.error as { message?: string; Message?: string; errors?: string[]; Errors?: string[] });
      if (msg) return msg;
    }
    if (err instanceof Error) return err.message;
    if (typeof err === 'string') return err;
    return this.i18n.t('common.request_failed');
  }

  clearLocalCart(): void {
    this.cartSubject.next(null);
  }
}
